import { useState, useMemo, useCallback, useEffect } from "react";
import { Download, Filter, Trash } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Select, Collapse, Pagination, Popconfirm } from "antd";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  DynamicModuleService,
  SchemaService,
  DropdownService,
  PtsProjectService,
} from "@/services/APIs";
import { get } from "@/services/axiosUitils";
import { getEmployeeId } from "@/lib/storeManager";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { downloadSpentHoursExcel } from "@/lib/exportSpentHoursExcel";
import { isEmployee, hasRole } from "../../utils/rolePermissions";
import { notify } from "@/components/common/Notification";
import CustomButton from "@/components/common/Button";
import { buildSpentHoursStatusOptions } from "../EmployeeDashboard/spentHoursStatusHelpers";
import { spentHoursStatusPill } from "../EmployeeDashboard/spentHoursStatusPill";
import "react-quill/dist/quill.snow.css";

const SPENT_HOURS_SCHEMA_NAME = "PTS Spent Hours";
const TASK_SCHEMA_NAME = "PTS Task";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const PTS_PROJECT_SCHEMA_NAME = "PTS Project";
/** Export fetches all filtered records in one API call. */
const SPENT_HOURS_EXPORT_PAGE_SIZE = 8000;

function normalizeApiDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  return value.split("T")[0] || null;
}

/** Prefer API `hour` / `minutetime` so `estimatedhours` is never used as logged time. */
function getLoggedHoursMinutesFromEntry(
  entry: any,
  hoursField: string | null,
  minutesField: string | null
): {
  hours: number | string | null | undefined;
  minutes: number | string | null | undefined;
} {
  const hasDiscreteHour =
    entry.hour !== undefined &&
    entry.hour !== null &&
    String(entry.hour).trim() !== "";
  if (hasDiscreteHour) {
    return {
      hours: entry.hour,
      minutes: entry.minutetime ?? entry.minutes ?? entry.total_minutes ?? 0,
    };
  }
  return {
    hours:
      (hoursField && entry[hoursField]) ??
      entry.hours ??
      entry.total_hours ??
      0,
    minutes:
      (minutesField && entry[minutesField]) ??
      entry.minutes ??
      entry.minutetime ??
      entry.total_minutes ??
      0,
  };
}

function getEstimatedHoursDisplayFromEntry(
  entry: any,
  estimatedHoursField: string | null
): string {
  const raw =
    (estimatedHoursField && entry[estimatedHoursField]) ??
    entry.estimatedhours ??
    entry.estimated_hours ??
    null;
  if (raw === null || raw === undefined || String(raw).trim() === "") {
    return "-";
  }
  return String(raw);
}

function getWorkedHoursDisplayFromEntry(
  entry: any,
  workedHoursField: string | null
): string {
  const raw =
    (workedHoursField && entry[workedHoursField]) ??
    entry.work_hours ??
    entry.worked_hours ??
    null;
  if (raw === null || raw === undefined || String(raw).trim() === "") {
    return "-";
  }
  return String(raw);
}

function parseHoursToMinutes(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const text = String(value).trim();
  if (!text || text === "-") {
    return null;
  }

  if (text.includes(":")) {
    const [hPart, mPart] = text.split(":");
    const h = Number(hPart);
    const m = Number(mPart);
    if (Number.isFinite(h) && Number.isFinite(m)) {
      return h * 60 + m;
    }
  }

  const numeric = Number(text.replace(/h/gi, "").trim());
  if (Number.isFinite(numeric)) {
    return Math.round(numeric * 60);
  }

  return null;
}

const SpentHoursPage = () => {
  const employeeId = getEmployeeId();

  // Check user role permissions
  const userIsEmployee = isEmployee();

  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [selectedTask, setSelectedTask] = useState<string>("all");

  // Reset task selection when project changes
  const handleProjectChange = (projectId?: string) => {
    setSelectedProject(projectId ?? "all");
    setSelectedTask("all"); // Reset task selection when project changes
  };

  // Get today's date in YYYY-MM-DD format (for HTML date input)
  const getTodayDate = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const [startDate, setStartDate] = useState<string>(() => getTodayDate());
  const [endDate, setEndDate] = useState<string>(() => getTodayDate());

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(500);
  const [isExporting, setIsExporting] = useState(false);

  // Set selected employee based on role
  // If employee role: always use their own employeeId (cannot change)
  // If admin/user role: default to "all" (can select any employee)
  const [selectedEmployee, setSelectedEmployee] = useState<string>(() => {
    if (userIsEmployee && employeeId) {
      return employeeId;
    }
    return "all"; // Default to "all" for admin/user roles
  });

  // Update selectedEmployee when employeeId is available (only for employee role)
  // For admin/user roles, allow manual selection changes
  useEffect(() => {
    if (userIsEmployee && employeeId) {
      // Lock to employee's own ID if they have employee role
      setSelectedEmployee(employeeId);
    }
  }, [employeeId, userIsEmployee]);

  const queryClient = useQueryClient();
  const userIsPTSAdmin =
    hasRole("PTS_Admin") || hasRole("PTS_Admin".toLowerCase());

  const deleteSpentHoursMutation = useMutation({
    mutationFn: async (id: string) => {
      return await DynamicModuleService.deleteItem(
        SPENT_HOURS_SCHEMA_NAME,
        String(id)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["spent-hours-data", SPENT_HOURS_SCHEMA_NAME],
      });
    },
    onError: (error: any) => {
      notify.error(error?.message ?? "Failed to delete time entry");
    },
  });

  // Format date for API (YYYY-MM-DDTHH:mm:ss)
  const formatDateForAPI = useCallback(
    (dateString: string, isEndDate: boolean = false) => {
      if (!dateString) {
        return null;
      }
      const [year, month, day] = dateString.split("-").map(Number);
      if (isEndDate) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T23:59:59`;
      } else {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
      }
    },
    []
  );

  // Fetch column grid data to find field names
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", SPENT_HOURS_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(SPENT_HOURS_SCHEMA_NAME),
  });

  const { data: spentHoursSchemaRecord } = useQuery({
    queryKey: ["schema", SPENT_HOURS_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(SPENT_HOURS_SCHEMA_NAME),
    enabled: !!SPENT_HOURS_SCHEMA_NAME,
  });

  // Fetch dropdowns
  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data: selectedProjectRecord } = useQuery({
    queryKey: ["spent-hours-pts-project", selectedProject],
    queryFn: () =>
      DynamicModuleService.getItemById(
        PTS_PROJECT_SCHEMA_NAME,
        selectedProject
      ),
    enabled: !!selectedProject && selectedProject !== "all",
  });

  useEffect(() => {
    if (!selectedProject || selectedProject === "all") {
      const today = getTodayDate();
      setStartDate(prev => (prev === today ? prev : today));
      return;
    }
    const record = selectedProjectRecord as Record<string, unknown> | undefined;
    const raw =
      (typeof record?.start_date === "string" && record.start_date) ||
      (typeof record?.startdate === "string" && record.startdate) ||
      (typeof record?.StartDate === "string" && record.StartDate) ||
      null;
    const projectStartDate = normalizeApiDate(raw);
    if (!projectStartDate) {
      return;
    }
    setStartDate(prev => (prev === projectStartDate ? prev : projectStartDate));
  }, [selectedProject, selectedProjectRecord, getTodayDate]);

  // Fetch all tasks for displaying task names in history
  const { data: allTaskOptions } = useQuery({
    queryKey: ["all-task-dropdown", TASK_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(TASK_SCHEMA_NAME),
    enabled: !!TASK_SCHEMA_NAME,
  });

  // Fetch tasks by project (for filter dropdown)
  const { data: taskOptions } = useQuery({
    queryKey: ["project-task-dropdown", selectedProject],
    queryFn: async () => {
      if (!selectedProject || selectedProject === "all") {
        return [];
      }
      const response = await get<any>("/pts_app/project-task/", {
        params: {
          project: selectedProject,
        },
      });

      // Format response to match dropdown format (value/label)
      // Response format: { success: true, tasks: [{ id, taskname }], count: number }
      if (
        response?.success &&
        response?.tasks &&
        Array.isArray(response.tasks)
      ) {
        return response.tasks.map((task: any) => ({
          value: task.id,
          label: task.taskname ?? task.name ?? String(task.id),
        }));
      }

      // Fallback: Handle if response is directly an array
      if (Array.isArray(response)) {
        return response.map((task: any) => ({
          value: task.id ?? task.value,
          label: task.taskname ?? task.name ?? task.label ?? String(task.id),
        }));
      }

      return [];
    },
    enabled: !!selectedProject && selectedProject !== "all",
  });

  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: async () => {
      // Old API (commented as requested):
      // return DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME);
      const response = await get<any>("/pts_app/full-employee-list/");
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      return rows
        .map((employee: any) => {
          const value = employee?.emp_id ?? employee?.id ?? employee?.value;
          const label = employee?.emp_name ?? employee?.name ?? employee?.label;

          if (!value) {
            return null;
          }
          return {
            value: String(value),
            label: label ? String(label) : String(value),
          };
        })
        .filter(Boolean) as { value: string; label: string }[];
    },
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Employee options are already returned as full names by /pts_app/full-employee-list/
  const employeeOptionsWithNames = useMemo(() => {
    return employeeOptions ?? [];
  }, [employeeOptions]);

  // Fetch spent hours data
  const { data: spentHoursDataResponse, isLoading } = useQuery({
    queryKey: [
      "spent-hours-data",
      SPENT_HOURS_SCHEMA_NAME,
      startDate,
      endDate,
      selectedProject,
      selectedTask,
      selectedEmployee,
      currentPage,
      pageSize,
    ],
    queryFn: () => {
      const paramsFilter: any = {};

      // Add date filters only if dates are selected
      if (startDate && startDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.date__gte = formattedStartDate;
        }
      }
      if (endDate && endDate.trim() !== "") {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.date__lte = formattedEndDate;
        }
      }

      // Add employee ID filter if a specific employee is selected (not "all")
      if (selectedEmployee && selectedEmployee !== "all") {
        // Try different field name variations for employee filter
        paramsFilter.emp_id__eq = selectedEmployee;
        // Alternative field names (uncomment if needed based on your API)
        // paramsFilter.employee__eq = selectedEmployee;
        // paramsFilter.employee_id__eq = selectedEmployee;
        // paramsFilter.empname__eq = selectedEmployee;
      }

      // Add project filter
      if (selectedProject && selectedProject !== "all") {
        paramsFilter.spentproject__eq = selectedProject;
      }

      // Add task filter
      if (selectedTask && selectedTask !== "all") {
        paramsFilter.spenttask__eq = selectedTask;
      }

      // Add ordering
      paramsFilter.ordering = "-date";

      // Use getItemsByPage to preserve full response with count for pagination
      return DynamicModuleService.getItemsByPage(
        SPENT_HOURS_SCHEMA_NAME,
        currentPage,
        pageSize,
        paramsFilter
      );
    },
    enabled: !!SPENT_HOURS_SCHEMA_NAME,
  });

  // Extract data from response
  const timeEntries = useMemo(() => {
    if (!spentHoursDataResponse) {
      return [];
    }
    if (Array.isArray(spentHoursDataResponse)) {
      return spentHoursDataResponse;
    }
    if (
      spentHoursDataResponse.results &&
      Array.isArray(spentHoursDataResponse.results)
    ) {
      return spentHoursDataResponse.results;
    }
    return [];
  }, [spentHoursDataResponse]);

  // Extract total count from response
  const totalCount = useMemo(() => {
    if (!spentHoursDataResponse) {
      return 0;
    }
    if (Array.isArray(spentHoursDataResponse)) {
      return spentHoursDataResponse.length;
    }
    if (
      typeof spentHoursDataResponse === "object" &&
      spentHoursDataResponse.count !== undefined
    ) {
      return spentHoursDataResponse.count;
    }
    if (
      spentHoursDataResponse.results &&
      Array.isArray(spentHoursDataResponse.results)
    ) {
      return (
        spentHoursDataResponse.count ?? spentHoursDataResponse.results.length
      );
    }
    return 0;
  }, [spentHoursDataResponse]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, selectedProject, selectedTask, selectedEmployee]);

  // Find field names from schema
  const findFieldName = useCallback(
    (searchTerms: string[], excludeNameSubstrings: string[] = []) => {
      if (!columnGridData?.grid) {
        return null;
      }
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          excludeNameSubstrings.some(
            ex => fieldName.includes(ex) ?? fieldTitle.includes(ex)
          )
        ) {
          continue;
        }
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [columnGridData]
  );

  // Get field names
  const dateField = findFieldName(["date", "work_date", "entry_date"]);
  const projectField = findFieldName(["spentproject", "project", "project_id"]);
  const taskField = findFieldName(["spenttask", "task", "task_id"]);
  const descriptionField = findFieldName(["description", "desc"]);
  const hoursField = findFieldName(
    ["spent_hours", "total_hours", "hours", "hour"],
    ["estimate"]
  );
  const minutesField = findFieldName([
    "minutes",
    "minutetime",
    "total_minutes",
  ]);
  const employeeField = findFieldName(["emp_id", "employee", "empname"]);
  const statusField = findFieldName(["status", "approval_status"]);
  /** Display-only sub task name returned directly by spent-hours rows. */
  const subtaskNameField = findFieldName(["subtask_name"]);
  const estimatedHoursField = findFieldName([
    "estimatedhours",
    "estimated_hours",
  ]);
  const workedHoursField = findFieldName(
    ["work_hours", "worked_hours", "workedhours", "worked hours"],
    ["estimate"]
  );

  const spentHoursStatusOptions = useMemo(
    () =>
      buildSpentHoursStatusOptions(
        spentHoursSchemaRecord,
        columnGridData?.grid,
        statusField ?? "status"
      ),
    [spentHoursSchemaRecord, columnGridData, statusField]
  );

  const getSubtaskNameDisplay = useCallback(
    (entry: any) => {
      const raw =
        (subtaskNameField && entry[subtaskNameField]) ??
        entry.subtask_name ??
        null;
      if (raw === null || raw === undefined || String(raw).trim() === "") {
        return "-";
      }
      return String(raw).trim();
    },
    [subtaskNameField]
  );

  // Helper functions to get names by ID
  const getProjectNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return "-";
      }
      const project = projectOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      return project?.label ?? id;
    },
    [projectOptions]
  );

  const getTaskNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return "-";
      }
      // First try to find in all tasks (for history display)
      const task = allTaskOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      if (task) {
        return task.label;
      }
      // Fallback to project-specific tasks (for filter dropdown)
      const projectTask = taskOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      return projectTask?.label ?? id;
    },
    [allTaskOptions, taskOptions]
  );

  // const getEmployeeNameById = useCallback(
  //   (id: string | number | null | undefined) => {
  //     if (!id) {
  //       return "-";
  //     }
  //     const employee = employeeOptions?.find(
  //       (item: { value: string; label: string }) =>
  //         String(item.value) === String(id)
  //     );
  //     return employee?.label ?? id;
  //   },
  //   [employeeOptions]
  // );

  // Get employee first and last name by ID
  const getEmployeeNamesById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return { firstName: "-", lastName: "" };
      }

      const dropdownEmployee = employeeOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      if (dropdownEmployee?.label) {
        const nameParts = dropdownEmployee.label.split(" ");
        return {
          firstName: nameParts[0] ?? "-",
          lastName: nameParts.slice(1).join(" ") ?? "",
        };
      }
      return { firstName: "-", lastName: "" };
    },
    [employeeOptions]
  );

  // Format hours and minutes
  const formatHours = (
    hours: number | string | null | undefined,
    minutes: number | string | null | undefined
  ) => {
    const h = hours ? Number(hours) : 0;
    const m = minutes ? Number(minutes) : 0;
    if (h === 0 && m === 0) {
      return "-";
    }
    if (m === 0) {
      return `${h}h`;
    }
    if (h === 0) {
      return `${m}m`;
    }
    return `${h}h ${m}m`;
  };

  // Format total hours for header (e.g., "10 Hrs. 00 Min")
  const formatTotalHours = (totalHours: number, totalMinutes: number) => {
    const hoursStr = String(Math.floor(totalHours)).padStart(2, "0");
    const minutesStr = String(totalMinutes).padStart(2, "0");
    return `${hoursStr} Hrs. ${minutesStr} Min`;
  };

  // Format hours for export as duration HH:MM (zero-padded, e.g. "05:03", "124:00")
  const formatHoursForCSV = (
    hours: number | string | null | undefined,
    minutes: number | string | null | undefined
  ) => {
    const h = hours ? Number(hours) : 0;
    const m = minutes ? Number(minutes) : 0;
    const totalMinutes = h * 60 + m;
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    return `${String(totalHours).padStart(2, "0")}:${String(remainingMinutes).padStart(2, "0")}`;
  };

  /** Export: all dates as DD-MM-YYYY (avoids timezone shift for YYYY-MM-DD API values). */
  const formatDateToDDMMYYYY = (
    dateString: string | null | undefined,
    whenEmpty: string
  ): string => {
    if (!dateString || String(dateString).trim() === "") {
      return whenEmpty;
    }
    const trimmed = String(dateString).trim();
    const ymdPart = trimmed.split("T")[0] ?? trimmed;
    const parts = ymdPart.split("-");
    if (
      parts.length === 3 &&
      parts[0].length === 4 &&
      /^\d+$/.test(parts[0]) &&
      /^\d+$/.test(parts[1]) &&
      /^\d+$/.test(parts[2])
    ) {
      const [y, m, d] = parts;
      return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
    }
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) {
      return [
        String(d.getDate()).padStart(2, "0"),
        String(d.getMonth() + 1).padStart(2, "0"),
        String(d.getFullYear()),
      ].join("-");
    }
    return trimmed;
  };

  /**
   * Export description in one cell with newlines preserved:
   * 1) Bullet lines — list items as "- …"
   * 2) Hyphen-separated lines — e.g. "URL - task title" kept as one line (hyphen is content, not a bullet)
   * 3) Normal paragraphs — plain lines; single blank lines between blocks kept
   */
  const descriptionToExportPlainText = (raw: unknown): string => {
    if (raw === null || raw === undefined || raw === "-") {
      return "-";
    }
    const s = String(raw).trim();
    if (!s) {
      return "-";
    }

    const finalizeText = (text: string): string =>
      text
        .replace(/\r\n/g, "\n")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n")
        .split("\n")
        .map(line => line.replace(/\s+/g, " ").trim())
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .replace(/^\n+|\n+$/g, "")
        .trim();

    const formatListItemBody = (body: string): string => {
      const t = body.replace(/\s+/g, " ").trim();
      if (!t) {
        return "";
      }
      // "http(s)://… - title" — hyphen separates URL from text; keep one line, no list bullet prefix
      if (/^https?:\/\/\S+/i.test(t) && /\s-\s/.test(t)) {
        return t;
      }
      if (/^[-•*]\s/.test(t)) {
        return /^•\s/.test(t) ? `- ${t.slice(1).trim()}` : t;
      }
      return `- ${t}`;
    };

    const serializeListBlock = (inner: string): string => {
      const items: string[] = [];
      const re = /<li[^>]*>([\s\S]*?)<\/li>/gi;
      let m: RegExpExecArray | null;
      while ((m = re.exec(inner)) !== null) {
        const plain = m[1]
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        const line = formatListItemBody(plain);
        if (line) {
          items.push(line);
        }
      }
      return items.length > 0 ? `${items.join("\n")}\n` : "";
    };

    /** SSR / string fallback: walk blocks in order so lists + paragraphs both export */
    const htmlToPlainStructuredSSR = (html: string): string => {
      let h = html.replace(/\r\n/g, "\n");
      h = h.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner) =>
        serializeListBlock(inner)
      );
      h = h.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner) =>
        serializeListBlock(inner)
      );
      h = h.replace(/<br\s*\/?>/gi, "\n");
      h = h.replace(/<\/(p|div|h1|h2|h3|h4|blockquote)>/gi, "\n");
      h = h.replace(/<[^>]+>/g, "");
      return finalizeText(h) || "-";
    };

    const looksLikeHtml = /<\/?[a-z][\s\S]*?>/i.test(s);
    if (!looksLikeHtml) {
      return finalizeText(s) || "-";
    }

    if (typeof document === "undefined") {
      return htmlToPlainStructuredSSR(s);
    }

    const div = document.createElement("div");
    div.innerHTML = s;

    const walk = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent ?? "").replace(/\s+/g, " ");
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return "";
      }
      const el = node as Element;
      const name = el.tagName.toLowerCase();

      if (name === "br") {
        return "\n";
      }
      if (name === "li") {
        const inner = Array.from(el.childNodes)
          .map(walk)
          .join("")
          .replace(/\s+/g, " ")
          .trim();
        const line = formatListItemBody(inner);
        return line ? `${line}\n` : "";
      }
      if (name === "ul" || name === "ol") {
        return Array.from(el.children)
          .filter(c => c.tagName.toLowerCase() === "li")
          .map(walk)
          .join("");
      }
      if (
        name === "p" ||
        name === "h1" ||
        name === "h2" ||
        name === "h3" ||
        name === "h4" ||
        name === "blockquote"
      ) {
        const inner = Array.from(el.childNodes)
          .map(walk)
          .join("")
          .replace(/[ \t]*\n[ \t]*/g, "\n")
          .trimEnd();
        return inner ? `${inner}\n` : "\n";
      }
      if (name === "div") {
        return Array.from(el.childNodes).map(walk).join("");
      }
      return Array.from(el.childNodes).map(walk).join("");
    };

    let text = Array.from(div.childNodes).map(walk).join("");
    text = finalizeText(text);
    return text || "-";
  };

  /** Parse work date for sorting. Uses calendar YYYY-MM-DD when present. */
  const getExportEntryDateMs = (entry: any): number => {
    const raw =
      (dateField && entry[dateField]) ?? entry.date ?? entry.work_date;
    if (!raw) {
      return 0;
    }
    const ymd = String(raw).split("T")[0] ?? "";
    const parts = ymd.split("-");
    if (
      parts.length === 3 &&
      parts[0].length === 4 &&
      parts.every(p => /^\d+$/.test(p))
    ) {
      const y = Number(parts[0]);
      const m = Number(parts[1]);
      const d = Number(parts[2]);
      return new Date(y, m - 1, d).getTime();
    }
    const t = new Date(String(raw)).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  // Group entries by date
  const groupedByDate = useMemo(() => {
    const groups: Record<
      string,
      {
        date: string;
        dateKey: string;
        entries: any[];
        totalHours: number;
        totalMinutes: number;
      }
    > = {};

    timeEntries.forEach((entry: any) => {
      const date =
        (dateField && entry[dateField]) ?? entry.date ?? entry.work_date;

      if (!date) {
        return;
      }

      // Format date as DD/MM/YYYY
      let dateStr = "-";
      try {
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = d.getFullYear();
          dateStr = `${day}/${month}/${year}`;
        } else {
          dateStr = String(date);
        }
      } catch {
        dateStr = String(date);
      }

      const dateKey = dateStr;

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: dateStr,
          dateKey,
          entries: [],
          totalHours: 0,
          totalMinutes: 0,
        };
      }

      const { hours, minutes } = getLoggedHoursMinutesFromEntry(
        entry,
        hoursField,
        minutesField
      );

      const h =
        hours === "" || hours === null || hours === undefined
          ? 0
          : Number(hours);
      const m =
        minutes === "" || minutes === null || minutes === undefined
          ? 0
          : Number(minutes);

      groups[dateKey].entries.push(entry);
      groups[dateKey].totalHours += h;
      groups[dateKey].totalMinutes += m;
    });

    // Convert minutes overflow to hours
    Object.values(groups).forEach(group => {
      const additionalHours = Math.floor(group.totalMinutes / 60);
      group.totalHours += additionalHours;
      group.totalMinutes = group.totalMinutes % 60;
    });

    // Sort by date (newest first)
    return Object.values(groups).sort((a, b) => {
      const dateA = a.entries[0]?.[dateField ?? "date"] ?? a.date;
      const dateB = b.entries[0]?.[dateField ?? "date"] ?? b.date;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [timeEntries, dateField, hoursField, minutesField]);

  // Prefer API `total_workhours` (e.g. "10:30"); fall back to summing current-page groups
  const totalHoursAll = useMemo(() => {
    const apiTotal =
      spentHoursDataResponse &&
      typeof spentHoursDataResponse === "object" &&
      !Array.isArray(spentHoursDataResponse)
        ? (spentHoursDataResponse as { total_workhours?: string })
            .total_workhours
        : undefined;

    if (
      apiTotal !== undefined &&
      apiTotal !== null &&
      String(apiTotal).trim()
    ) {
      const totalMinutes = parseHoursToMinutes(apiTotal);
      if (totalMinutes !== null) {
        return {
          totalHours: Math.floor(totalMinutes / 60),
          totalMinutes: totalMinutes % 60,
        };
      }
    }

    let totalH = 0;
    let totalM = 0;

    groupedByDate.forEach(group => {
      totalH += group.totalHours;
      totalM += group.totalMinutes;
    });

    const additionalHours = Math.floor(totalM / 60);
    totalH += additionalHours;
    totalM = totalM % 60;

    return { totalHours: totalH, totalMinutes: totalM };
  }, [spentHoursDataResponse, groupedByDate]);

  // Handle export - single API call with page_size 8000 for all filtered data
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const paramsFilter: any = {};

      if (startDate && startDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.date__gte = formattedStartDate;
        }
      }
      if (endDate && endDate.trim() !== "") {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.date__lte = formattedEndDate;
        }
      }

      if (selectedEmployee && selectedEmployee !== "all") {
        paramsFilter.emp_id__eq = selectedEmployee;
      }

      if (selectedProject && selectedProject !== "all") {
        paramsFilter.spentproject__eq = selectedProject;
      }

      if (selectedTask && selectedTask !== "all") {
        paramsFilter.spenttask__eq = selectedTask;
      }

      paramsFilter.ordering = "date";

      const exportResponse = await DynamicModuleService.getItemsByPage(
        SPENT_HOURS_SCHEMA_NAME,
        1,
        SPENT_HOURS_EXPORT_PAGE_SIZE,
        paramsFilter
      );

      let allEntries: any[] = [];
      let apiTotalWorkhours: string | undefined;

      if (Array.isArray(exportResponse)) {
        allEntries = exportResponse;
      } else if (
        exportResponse?.results &&
        Array.isArray(exportResponse.results)
      ) {
        allEntries = exportResponse.results;
        if (exportResponse.total_workhours !== undefined) {
          apiTotalWorkhours = exportResponse.total_workhours;
        }
      }

      if (allEntries.length === 0) {
        notify.error("No data available to export");
        return;
      }

      // Oldest date first (e.g. 01 before 31) regardless of API order
      allEntries.sort(
        (a, b) => getExportEntryDateMs(a) - getExportEntryDateMs(b)
      );

      // Calculate total hours and minutes
      let totalHours = 0;
      let totalMinutes = 0;

      // Format export: Date, Project, Task, Sub task, Estimated hours, Worked hours, Employee, Hours, Status, Description
      const exportData = allEntries.map((entry: any) => {
        const projectId =
          (projectField && entry[projectField]) ??
          entry.project_id ??
          entry.project ??
          entry.spentproject;
        const taskId =
          (taskField && entry[taskField]) ??
          entry.task_id ??
          entry.task ??
          entry.spenttask;
        const employeeIdValue =
          (employeeField && entry[employeeField]) ??
          entry.emp_id ??
          entry.employee;
        const { hours, minutes } = getLoggedHoursMinutesFromEntry(
          entry,
          hoursField,
          minutesField
        );
        const taskName = getTaskNameById(taskId);
        const description =
          (descriptionField && entry[descriptionField]) ??
          entry.description ??
          entry.desc ??
          "-";
        const subTaskName = getSubtaskNameDisplay(entry);
        const statusRaw =
          (statusField && entry[statusField]) ?? entry.status ?? null;
        const statusLabelForExport =
          statusRaw !== null && String(statusRaw).trim() !== ""
            ? (spentHoursStatusOptions.find(
                o => String(o.value) === String(statusRaw)
              )?.label ?? String(statusRaw))
            : "-";

        // Get date from entry
        const entryDate =
          (dateField && entry[dateField]) ?? entry.date ?? entry.work_date;

        // Calculate totals
        const h =
          hours === "" || hours === null || hours === undefined
            ? 0
            : Number(hours);
        const m =
          minutes === "" || minutes === null || minutes === undefined
            ? 0
            : Number(minutes);
        totalHours += h;
        totalMinutes += m;

        // Get employee name
        const employeeNames = getEmployeeNamesById(employeeIdValue);
        const userName =
          `${employeeNames.firstName} ${employeeNames.lastName}`.trim() || "-";

        const cleanDescription = descriptionToExportPlainText(description);

        return {
          Date: formatDateToDDMMYYYY(entryDate, "-"),
          Project: getProjectNameById(projectId),
          Task: taskName ?? "-",
          "Sub task": subTaskName,
          "Estimated hours": getEstimatedHoursDisplayFromEntry(
            entry,
            estimatedHoursField
          ),
          "Worked hours": getWorkedHoursDisplayFromEntry(
            entry,
            workedHoursField
          ),
          Employee: userName,
          Hours: formatHoursForCSV(hours, minutes),
          Status: statusLabelForExport,
          Description: cleanDescription,
        };
      });

      const additionalHours = Math.floor(totalMinutes / 60);
      totalHours += additionalHours;
      totalMinutes = totalMinutes % 60;

      let exportTotalHours = totalHours;
      let exportTotalMinutes = totalMinutes;
      if (
        apiTotalWorkhours !== undefined &&
        apiTotalWorkhours !== null &&
        String(apiTotalWorkhours).trim()
      ) {
        const apiTotalMinutes = parseHoursToMinutes(apiTotalWorkhours);
        if (apiTotalMinutes !== null) {
          exportTotalHours = Math.floor(apiTotalMinutes / 60);
          exportTotalMinutes = apiTotalMinutes % 60;
        }
      }

      // Get filter values for header
      const projectName =
        selectedProject === "all" ? "All" : getProjectNameById(selectedProject);
      const categoryName = "All"; // No category filter in UI, so always "All"
      const taskTypeName = "All"; // No task type filter in UI, so always "All"
      const employeeName =
        selectedEmployee === "all"
          ? "All"
          : (() => {
              const names = getEmployeeNamesById(selectedEmployee);
              return `${names.firstName} ${names.lastName}`.trim() || "All";
            })();

      const headers = [
        "Date",
        "Project",
        "Task",
        "Sub task",
        "Estimated hours",
        "Worked hours",
        "Employee",
        "Hours",
        "Status",
        "Description",
      ];

      const metaRows: [string, string][] = [
        ["Start Date", formatDateToDDMMYYYY(startDate, "All")],
        ["End Date", formatDateToDDMMYYYY(endDate, "All")],
        ["Project Name", String(projectName)],
        ["Category", String(categoryName)],
        ["Task Type", String(taskTypeName)],
        ["Employee Name", String(employeeName)],
        [
          "Total Hours",
          formatHoursForCSV(exportTotalHours, exportTotalMinutes),
        ],
      ];

      const dataRows = exportData.map(row =>
        headers.map(header => String(row[header as keyof typeof row] ?? ""))
      );

      const now = new Date();
      const baseFilename = `spent-hours-export-${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;

      await downloadSpentHoursExcel({
        metaRows,
        columnHeaders: headers,
        dataRows,
        baseFilename,
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      notify.error("Failed to export spent hours data");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Style for rendered ReactQuill HTML content in spent hours */
        .ql-editor-content {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .ql-editor-content p {
          margin: 0 0 8px 0;
          line-height: 1.6;
        }
        
        .ql-editor-content p:last-child {
          margin-bottom: 0;
        }
        
        .ql-editor-content ul,
        .ql-editor-content ol {
          margin: 8px 0;
          padding-left: 24px;
        }
        
        .ql-editor-content li {
          margin: 4px 0;
          line-height: 1.6;
        }
        
        .ql-editor-content strong {
          font-weight: 600;
        }
        
        .ql-editor-content em {
          font-style: italic;
        }
        
        .ql-editor-content u {
          text-decoration: underline;
        }
        
        .ql-editor-content s {
          text-decoration: line-through;
        }
        
        .ql-editor-content a {
          color: #3b82f6;
          text-decoration: underline;
        }
        
        .ql-editor-content a:hover {
          color: #2563eb;
        }
        
        .ql-editor-content h1,
        .ql-editor-content h2,
        .ql-editor-content h3 {
          margin: 12px 0 8px 0;
          font-weight: 600;
        }
        
        .ql-editor-content h1 {
          font-size: 1.5em;
        }
        
        .ql-editor-content h2 {
          font-size: 1.3em;
        }
        
        .ql-editor-content h3 {
          font-size: 1.1em;
        }
      `}</style>
      <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
        {/* Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
              Spent Hours
            </h1>
            <p className="text-sm text-[#627384]">
              Log and track time spent on projects and tasks.
            </p>
          </div>
          <div className="flex items-center gap-4">
            {totalCount > 0 && (
              <Button
                onClick={handleExport}
                disabled={isExporting}
                className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
              >
                <Download size={16} />
                {isExporting ? "Exporting..." : "Export"}
              </Button>
            )}
          </div>
        </div>

        {/* Filter Time Entries Section */}
        <Card className="rounded-xl border bg-white">
          <CardContent className="p-3 md:p-4">
            <div className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-700">
              <Filter size={16} />
              Filter Time Entries
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-5">
              <div className="space-y-2">
                <Label htmlFor="project" className="text-sm font-medium">
                  Project
                </Label>
                <Select
                  id="project"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.trim().toLowerCase())
                  }
                  value={selectedProject}
                  onChange={handleProjectChange}
                  className="w-full"
                  placeholder="All Projects"
                  options={[
                    { value: "all", label: "All Projects" },
                    ...(projectOptions?.map(
                      (project: { value: string; label: string }) => ({
                        value: project.value,
                        label: project.label,
                      })
                    ) ?? []),
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task" className="text-sm font-medium">
                  Task
                </Label>
                <Select
                  id="task"
                  allowClear
                  value={selectedTask}
                  onChange={setSelectedTask}
                  className="w-full"
                  placeholder={
                    selectedProject === "all"
                      ? "Select a project first"
                      : "All Tasks"
                  }
                  disabled={selectedProject === "all"}
                  options={[
                    { value: "all", label: "All Tasks" },
                    ...(taskOptions?.map(
                      (task: { value: string; label: string }) => ({
                        value: task.value,
                        label: task.label,
                      })
                    ) ?? []),
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="start-date" className="text-sm font-medium">
                  Start Date
                </Label>
                <input
                  type="date"
                  id="start-date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value || "")}
                  className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 text-sm"
                  placeholder="dd-mm-yyyy"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date" className="text-sm font-medium">
                  End Date
                </Label>
                <input
                  type="date"
                  id="end-date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value || "")}
                  className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 text-sm"
                  placeholder="dd-mm-yyyy"
                />
              </div>
              <div className="flex flex-col items-center justify-center gap-1 md:ml-4 md:border-l">
                <span className="text-sm font-medium text-gray-700">
                  Total Hours
                </span>
                <span className="text-primary text-[16px] font-semibold">
                  {formatTotalHours(
                    totalHoursAll.totalHours,
                    totalHoursAll.totalMinutes
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Time Entry History Section */}
        <Card className="rounded-xl border bg-white">
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
              <h3 className="text-base font-semibold text-gray-900">
                Time Entry History
              </h3>
              <div className="flex flex-wrap items-center gap-3">
                {/* Employee filter - Only show for admin/user roles, hide for employee role */}
                {!userIsEmployee && (
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="employee-filter"
                      className="whitespace-nowrap text-sm text-gray-700"
                    >
                      Employee:
                    </Label>
                    <Select
                      id="employee-filter"
                      allowClear
                      value={selectedEmployee}
                      onChange={setSelectedEmployee}
                      placeholder="All Employees"
                      className="w-[180px]"
                      showSearch
                      filterOption={(input, option) => {
                        const label = String(option?.label ?? "");
                        return label
                          .toLowerCase()
                          .includes(input.toLowerCase());
                      }}
                      options={[
                        { value: "all", label: "All Employees" },
                        ...(employeeOptionsWithNames?.map(
                          (employee: { value: string; label: string }) => ({
                            value: employee.value,
                            label: employee.label,
                          })
                        ) ?? []),
                      ]}
                    />
                  </div>
                )}
                <span className="text-sm text-gray-500">
                  {totalCount} Records
                </span>
              </div>
            </div>

            {timeEntries.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No time entries found
              </div>
            ) : (
              <Collapse
                defaultActiveKey={
                  groupedByDate.length > 0 ? [groupedByDate[0].dateKey] : []
                }
                items={groupedByDate.map(group => ({
                  key: group.dateKey,
                  label: (
                    <div className="flex w-full items-center justify-between pr-4">
                      <span className="text-primary font-medium">
                        {group.date} : Total -{" "}
                        {formatTotalHours(group.totalHours, group.totalMinutes)}
                      </span>
                    </div>
                  ),
                  children: (
                    <div className="overflow-x-auto py-2">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            {/* <TableHead className="w-12 ps-2">
                            <input
                              type="radio"
                              name={`row-${group.dateKey}`}
                              className="h-4 w-4"
                            />
                          </TableHead> */}
                            <TableHead className="font-semibold text-gray-700">
                              Project
                            </TableHead>
                            <TableHead className="font-semibold text-gray-700">
                              Task
                            </TableHead>
                            <TableHead className="font-semibold text-gray-700">
                              Sub task
                            </TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">
                              Estimated hours
                            </TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">
                              Worked hours
                            </TableHead>
                            <TableHead className="font-semibold text-gray-700">
                              Employee
                            </TableHead>
                            <TableHead className="font-semibold text-gray-700">
                              Hours
                            </TableHead>
                            <TableHead className="font-semibold text-gray-700">
                              Status
                            </TableHead>
                            <TableHead className="font-semibold text-gray-700">
                              Description
                            </TableHead>
                            {userIsPTSAdmin && (
                              <TableHead className="w-24 font-semibold text-gray-700">
                                Actions
                              </TableHead>
                            )}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.entries.map((entry: any) => {
                            const recordId =
                              entry?.id ?? entry?.ID ?? entry?.Id ?? entry?._id;
                            const projectId =
                              (projectField && entry[projectField]) ??
                              entry.project_id ??
                              entry.project ??
                              entry.spentproject;
                            const taskId =
                              (taskField && entry[taskField]) ??
                              entry.task_id ??
                              entry.task ??
                              entry.spenttask;
                            const description =
                              (descriptionField && entry[descriptionField]) ??
                              entry.description ??
                              entry.desc ??
                              "-";
                            const { hours, minutes } =
                              getLoggedHoursMinutesFromEntry(
                                entry,
                                hoursField,
                                minutesField
                              );
                            const employeeIdValue =
                              (employeeField && entry[employeeField]) ??
                              entry.emp_id ??
                              entry.employee;
                            const statusRaw =
                              (statusField && entry[statusField]) ??
                              entry.status ??
                              null;
                            const statusLabelResolved =
                              statusRaw !== null &&
                              String(statusRaw).trim() !== ""
                                ? (spentHoursStatusOptions.find(
                                    o => String(o.value) === String(statusRaw)
                                  )?.label ?? String(statusRaw))
                                : null;
                            const estimatedDisplay =
                              getEstimatedHoursDisplayFromEntry(
                                entry,
                                estimatedHoursField
                              );
                            const workedDisplay =
                              getWorkedHoursDisplayFromEntry(
                                entry,
                                workedHoursField
                              );
                            const estimatedMinutes =
                              parseHoursToMinutes(estimatedDisplay);
                            const workedMinutes =
                              parseHoursToMinutes(workedDisplay);
                            const workedHoursColorClass =
                              estimatedMinutes !== null &&
                              workedMinutes !== null &&
                              workedMinutes > estimatedMinutes
                                ? "text-red-600"
                                : "text-gray-700";

                            return (
                              <TableRow
                                key={recordId}
                                className="hover:bg-gray-50"
                              >
                                {/* <TableCell>
                                <input
                                  type="radio"
                                  name={`row-${group.dateKey}`}
                                  className="h-4 w-4"
                                />
                              </TableCell> */}
                                <TableCell className="ps-4 text-gray-700">
                                  {getProjectNameById(projectId)}
                                </TableCell>
                                <TableCell className="text-gray-700">
                                  {getTaskNameById(taskId)}
                                </TableCell>
                                <TableCell className="max-w-[12rem] text-gray-700">
                                  {getSubtaskNameDisplay(entry)}
                                </TableCell>
                                <TableCell className="text-center text-gray-700">
                                  {estimatedDisplay}
                                </TableCell>
                                <TableCell
                                  className={`text-center ${workedHoursColorClass}`}
                                >
                                  {workedDisplay}
                                </TableCell>
                                <TableCell className="text-gray-700">
                                  {(() => {
                                    const names =
                                      getEmployeeNamesById(employeeIdValue);
                                    return (
                                      `${names.firstName} ${names.lastName}`.trim() ||
                                      "-"
                                    );
                                  })()}
                                </TableCell>
                                <TableCell className="font-medium text-gray-900">
                                  {formatHours(hours, minutes)}
                                </TableCell>
                                <TableCell>
                                  {statusLabelResolved ? (
                                    spentHoursStatusPill(
                                      statusLabelResolved,
                                      String(statusRaw)
                                    )
                                  ) : (
                                    <span className="pl-6">--</span>
                                  )}
                                </TableCell>
                                <TableCell className="max-w-xs text-gray-600">
                                  {description && description !== "-" ? (
                                    <div
                                      className="ql-editor-content prose prose-sm max-w-none"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          typeof description === "string"
                                            ? description
                                            : String(description),
                                      }}
                                      style={{
                                        fontSize: "14px",
                                        lineHeight: "1.6",
                                        color: "#374151",
                                      }}
                                    />
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                                {userIsPTSAdmin && (
                                  <TableCell className="w-24">
                                    <Popconfirm
                                      title="Are you sure you want to delete this time entry?"
                                      onConfirm={() => {
                                        if (recordId) {
                                          deleteSpentHoursMutation.mutate(
                                            String(recordId)
                                          );
                                        }
                                      }}
                                      okText="Yes"
                                      cancelText="No"
                                    >
                                      <CustomButton
                                        danger
                                        icon={<Trash size={16} />}
                                        type="text"
                                        title="Delete"
                                        disabled={
                                          deleteSpentHoursMutation.isPending
                                        }
                                      />
                                    </Popconfirm>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ),
                }))}
                className="border-0 bg-transparent"
              />
            )}
            {/* Pagination - Always show if there are records or loading */}
            <div className="flex items-center justify-end border-t p-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(page, size) => {
                  // This will trigger a new API call because currentPage/pageSize are in queryKey
                  if (size && size !== pageSize) {
                    // Reset to page 1 when page size changes
                    setCurrentPage(1);
                    setPageSize(size);
                  } else {
                    setCurrentPage(page);
                  }
                }}
                onShowSizeChange={(_current, size) => {
                  // Handle page size change separately
                  setCurrentPage(1);
                  setPageSize(size);
                }}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                pageSizeOptions={[
                  "50",
                  "100",
                  "200",
                  "300",
                  "500",
                  "800",
                  "1000",
                ]}
                disabled={isLoading}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default SpentHoursPage;
